/**
 * Internal Tracking console (white-label).
 *
 * Daily end-of-day review that replaces the Google Sheet: active Self shipments
 * with their current customer update and the SUGGESTED next update. Staff edit
 * and Approve (publishes to the customer timeline) or Reject — nothing reaches
 * the customer until approved. Rows expand to show the vendor timeline beside
 * ours, box details, and a manual update form.
 */
import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNotification } from "context/notification.context";
import PhrasesModal from "./components/PhrasesModal";
import VendorsModal from "./components/VendorsModal";
import RowDetail from "./components/RowDetail";
import {
  getInternalBoard,
  getInternalStats,
  refreshAllInternal,
  refreshInternal,
  approveInternal,
  rejectInternal,
  syncAwbsInternal,
  setInternalAwb,
  setInternalVendor,
  getTrackingVendors,
} from "network/service";

const STATUS_OPTIONS = ["Pending", "Picked Up", "Intransit", "On Hold", "Delivered", "Issue"];

/** Logged-in user id, for the approve/reject audit trail. */
const currentUserId = () => {
  try {
    return (JSON.parse(window.localStorage.getItem("MomoDoc") || "{}") || {})._id || null;
  } catch (e) {
    return null;
  }
};

const StatCard = ({ label, value, color }) => (
  <Grid item xs={6} md={3} lg={12 / 7}>
    <Card sx={{ p: 1.5, textAlign: "center" }}>
      <MDTypography variant="h5" fontWeight="bold" color={color || "dark"}>{value}</MDTypography>
      <MDTypography variant="caption" color="text">{label}</MDTypography>
    </Card>
  </Grid>
);

export default function InternalTracking() {
  const { showNotification } = useNotification();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [phrasesOpen, setPhrasesOpen] = useState(false);
  const [vendorsOpen, setVendorsOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [edits, setEdits] = useState({});
  const [awbEdits, setAwbEdits] = useState({});
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("active"); // active | delivered
  const [countryFilter, setCountryFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [days, setDays] = useState("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = (withDelivered = tab === "delivered") => {
    setLoading(true);
    getInternalBoard(withDelivered)
      .then((r) => {
        const data = r.data || [];
        setRows(data);
        const e = {};
        data.forEach((row) => {
          if (row.suggestion) {
            e[row.suggestion._id] = {
              message: row.suggestion.suggested_message || "",
              status: row.suggestion.suggested_status || "Intransit",
            };
          }
        });
        setEdits(e);
      })
      .catch(() => showNotification("error", "Error", "Failed to load internal tracking board"))
      .finally(() => setLoading(false));
    getInternalStats().then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    getTrackingVendors().then((r) => setVendors(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vendorInfo = (key) => vendors.find((v) => (v.key || "").toLowerCase() === String(key || "").toLowerCase());

  const generate = () => {
    setGenerating(true);
    refreshAllInternal()
      .then((r) => { showNotification("success", "Suggestions ready", `${r.data.created} new suggestions`); load(); })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Failed"))
      .finally(() => setGenerating(false));
  };

  const syncAwbs = () => {
    setSyncing(true);
    syncAwbsInternal()
      .then((r) => {
        showNotification("success", "Synced", `${r.data.updated} AWBs, ${r.data.vendor_set || 0} vendors filled`);
        load();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Sync failed"))
      .finally(() => setSyncing(false));
  };

  const setBusyFor = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  /** Update one row in place so highlights/labels react without a page refresh. */
  const patchRow = (parcelId, patch) =>
    setRows((rs) => rs.map((r) => (r.parcel_id === parcelId ? { ...r, ...patch } : r)));

  const saveVendor = (row, key) => {
    setInternalVendor({ id: row.parcel_id, vendor: key })
      .then(() => {
        showNotification("success", "Saved", "Vendor updated");
        patchRow(row.parcel_id, { internal_vendor: key });
      })
      .catch(() => showNotification("error", "Error", "Failed to save vendor"));
  };

  const saveAwb = (row, value) => {
    const v = (value || "").trim();
    if (v === (row.vendor_awb || "")) return;
    setInternalAwb({ id: row.parcel_id, awb: v })
      .then(() => {
        showNotification("success", "Saved", "Vendor AWB updated");
        patchRow(row.parcel_id, { vendor_awb: v });
      })
      .catch(() => showNotification("error", "Error", "Failed to save AWB"));
  };

  const suggestOne = (row) => {
    setBusyFor(row.parcel_id, true);
    refreshInternal({ id: row.parcel_id })
      .then(() => load())
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Failed"))
      .finally(() => setBusyFor(row.parcel_id, false));
  };

  const approve = (row, override) => {
    const s = row.suggestion;
    const e = override || edits[s._id] || {};
    setBusyFor(s._id, true);
    approveInternal({ id: s._id, status: e.status || s.suggested_status, message: e.message ?? s.suggested_message, reviewed_by: currentUserId() })
      .then((r) => {
        showNotification("success", "Published", r.data?.notified ? "Published — customer notified" : "Published to customer tracking");
        load();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Approve failed"))
      .finally(() => setBusyFor(s._id, false));
  };

  const reject = (row) => {
    setBusyFor(row.suggestion._id, true);
    rejectInternal({ id: row.suggestion._id, reviewed_by: currentUserId() })
      .then(() => load())
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Reject failed"))
      .finally(() => setBusyFor(row.suggestion._id, false));
  };

  const approveAll = () => {
    const pending = visible.filter((r) => r.suggestion);
    if (!pending.length) return;
    const incomplete = pending.filter((r) => !r.internal_vendor || !r.vendor_awb).length;
    const warning = incomplete
      ? `

⚠ ${incomplete} of them have no vendor / vendor AWB, so their updates aren't backed by real courier tracking.`
      : "";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Approve and publish ${pending.length} suggestions?${warning}`)) return;
    setGenerating(true);
    Promise.all(
      pending.map((row) => {
        const e = edits[row.suggestion._id] || {};
        return approveInternal({
          id: row.suggestion._id,
          status: e.status || row.suggestion.suggested_status,
          message: e.message ?? row.suggestion.suggested_message,
          reviewed_by: currentUserId(),
        }).catch(() => null);
      })
    ).then(() => { showNotification("success", "Published", `${pending.length} updates published`); load(); })
      .finally(() => setGenerating(false));
  };

  const staleColor = (d) => (d == null ? "text" : d >= 3 ? "error" : d >= 2 ? "warning" : "text");

  const row_created = (r) => r.created_at || null;

  const countries = Array.from(new Set(rows.map((r) => r.country).filter(Boolean))).sort();

  const visible = rows.slice().sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).filter((r) => {
    // Delivered shipments live on their own tab.
    if (tab === "delivered" ? r.status !== 3 : r.status === 3) return false;
    // state filter
    if (filter === "pending" && !r.suggestion) return false;
    if (filter === "stale" && (r.days_since_update ?? 99) < 3) return false;
    if (filter === "no_update" && r.days_since_update != null) return false;
    if (filter === "needs_setup" && r.internal_vendor && r.vendor_awb) return false;
    // country / vendor
    if (countryFilter !== "all" && r.country !== countryFilter) return false;
    if (vendorFilter !== "all" && (r.internal_vendor || "").toLowerCase() !== vendorFilter) return false;
    // last-update age
    if (days !== "all") {
      const d = r.days_since_update;
      if (days === "none" ? d != null : !(d != null && d <= Number(days))) return false;
    }
    // shipment created-date range
    if (fromDate || toDate) {
      if (!row_created(r)) return false;
      const c = new Date(row_created(r));
      if (fromDate && c < new Date(`${fromDate}T00:00:00`)) return false;
      if (toDate && c > new Date(`${toDate}T23:59:59`)) return false;
    }
    // free text
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${r.capex_awb} ${r.vendor_awb} ${r.forwarding_no} ${r.customer} ${r.country}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        {stats && (
          <Grid container spacing={1.5} mb={2}>
            <StatCard label="Active" value={stats.active} />
            <StatCard label="In transit" value={stats.in_transit} color="info" />
            <StatCard label="At customs" value={stats.at_customs} color="warning" />
            <StatCard label="Stale 3d+" value={stats.stale} color="error" />
            <StatCard label="No update" value={stats.no_update} color="secondary" />
            <StatCard label="Pending" value={stats.pending_suggestions} color="info" />
            <StatCard label="Delivered 30d" value={stats.delivered_30d} color="success" />
          </Grid>
        )}

        <Card>
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setExpanded(null); load(v === "delivered"); }}
            sx={{ px: 2, pt: 1, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab value="active" label="Active shipments" />
            <Tab value="delivered" label="Delivered" />
          </Tabs>
          <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold">Internal Tracking</MDTypography>
              <MDTypography variant="caption" color="text">
                {visible.length} {tab === "delivered" ? "delivered shipments" : "shipments — nothing reaches the customer until approved"}
              </MDTypography>
            </MDBox>
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <Select size="small" value={filter} sx={{ minWidth: 130, height: 38 }} onChange={(e) => setFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Has suggestion</MenuItem>
                <MenuItem value="stale">Stale 3d+</MenuItem>
                <MenuItem value="no_update">No update</MenuItem>
                <MenuItem value="needs_setup">Missing vendor / AWB</MenuItem>
              </Select>
              <Select size="small" value={vendorFilter} sx={{ minWidth: 120, height: 38 }} onChange={(e) => setVendorFilter(e.target.value)}>
                <MenuItem value="all">All vendors</MenuItem>
                {vendors.map((v) => <MenuItem key={v._id} value={(v.key || "").toLowerCase()}>{v.name}</MenuItem>)}
              </Select>
              <Select size="small" value={countryFilter} sx={{ minWidth: 130, height: 38 }} onChange={(e) => setCountryFilter(e.target.value)}>
                <MenuItem value="all">All countries</MenuItem>
                {countries.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
              <Select size="small" value={days} sx={{ minWidth: 130, height: 38 }} onChange={(e) => setDays(e.target.value)}>
                <MenuItem value="all">Any last update</MenuItem>
                <MenuItem value="0">Updated today</MenuItem>
                <MenuItem value="2">Within 2 days</MenuItem>
                <MenuItem value="7">Within 7 days</MenuItem>
                <MenuItem value="none">Never updated</MenuItem>
              </Select>
              <MDInput
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                sx={{ minWidth: 140 }}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <MDInput
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                sx={{ minWidth: 140 }}
                onChange={(e) => setToDate(e.target.value)}
              />
              <MDInput
                placeholder="Search AWB / customer"
                value={search}
                sx={{ minWidth: 180 }}
                onChange={(e) => setSearch(e.target.value)}
              />
              <MDButton variant="outlined" color="secondary" onClick={load} disabled={loading}>
                <Icon>refresh</Icon>&nbsp;Reload
              </MDButton>
              <MDButton variant="outlined" color="dark" onClick={() => setVendorsOpen(true)}>
                <Icon>local_shipping</Icon>&nbsp;Vendors
              </MDButton>
              <MDButton variant="outlined" color="dark" onClick={() => setPhrasesOpen(true)}>
                <Icon>edit_note</Icon>&nbsp;Phrases
              </MDButton>
              <MDButton variant="outlined" color="warning" onClick={syncAwbs} disabled={syncing}>
                <Icon>sync</Icon>&nbsp;{syncing ? "Syncing…" : "Sync sheet"}
              </MDButton>
              {tab === "active" && (
                <>
                  <MDButton variant="gradient" color="info" onClick={generate} disabled={generating}>
                    <Icon>auto_awesome</Icon>&nbsp;{generating ? "Working…" : "Generate suggestions"}
                  </MDButton>
                  <MDButton variant="gradient" color="success" onClick={approveAll} disabled={generating}>
                    <Icon>done_all</Icon>&nbsp;Approve all
                  </MDButton>
                </>
              )}
            </MDBox>
          </MDBox>

          <MDBox px={2} pb={2} sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {["", "#", "Created", "Last update", "Capex AWB", "Receiver", "Customer", "Country", "Branch", "Vendor", "Vendor AWB", "Wt", "Box", "Current update"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: "bold" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((row, idx) => {
                  const s = row.suggestion;
                  const e = (s && edits[s._id]) || {};
                  const rowBusy = busy[s ? s._id : row.parcel_id];
                  const vi = vendorInfo(row.internal_vendor);
                  const isOpen = expanded === row.parcel_id;
                  // No vendor or no vendor AWB => we can't pull real tracking
                  // for it, so flag the row for staff to complete.
                  const needsSetup = !row.internal_vendor || !row.vendor_awb;
                  return [
                    <TableRow
                      key={row.parcel_id}
                      hover
                      sx={
                        isOpen
                          ? {
                              backgroundColor: "#eaf3ff",
                              boxShadow: "inset 0 0 0 2px #1A73E8",
                              "& td": { borderBottom: "none" },
                            }
                          : needsSetup
                          ? {
                              backgroundColor: "#fff8e1",
                              boxShadow: "inset 3px 0 0 #ed6c02",
                            }
                          : {}
                      }
                    >
                      <TableCell sx={{ width: 56 }}>
                        <MDBox display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => setExpanded(isOpen ? null : row.parcel_id)}>
                            <Icon>{isOpen ? "expand_less" : "expand_more"}</Icon>
                          </IconButton>
                          {/* the suggestion now lives in the expansion — flag it here */}
                          {s ? (
                            <MDBox
                              title={`Suggested: ${s.suggested_message}`}
                              sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#1A73E8" }}
                            />
                          ) : null}
                        </MDBox>
                      </TableCell>
                      <TableCell sx={{ width: 46 }}>
                        <MDTypography variant="caption" color="text">{idx + 1}</MDTypography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        {row.created_at ? (
                          <>
                            <MDTypography variant="button" fontWeight="bold" display="block">
                              {new Date(row.created_at).toLocaleDateString()}
                            </MDTypography>
                            <MDTypography variant="button" fontWeight="bold" color="text">
                              {new Date(row.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </MDTypography>
                          </>
                        ) : "—"}
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        {row.last_date ? (
                          <>
                            <MDTypography
                              variant="button"
                              fontWeight="bold"
                              display="block"
                              color={staleColor(row.days_since_update)}
                            >
                              {new Date(row.last_date).toLocaleDateString()}
                            </MDTypography>
                            <MDTypography variant="button" fontWeight="bold" color="text">
                              {new Date(row.last_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </MDTypography>
                          </>
                        ) : (
                          <MDTypography variant="caption" color="text">never</MDTypography>
                        )}
                      </TableCell>
                      <TableCell>
                        <a
                          href={`https://capexcourier.com/track/?tracking=${row.capex_awb}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontWeight: 700, fontSize: "0.875rem" }}
                        >
                          {row.capex_awb || row.code}
                        </a>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="bold">{row.receiver || "—"}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="bold">{row.customer || "—"}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="bold">{row.country || "—"}</MDTypography>
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                          {row.transit ? <Chip label={row.transit} size="small" sx={{ height: 16, fontSize: "0.6rem" }} /> : null}
                          {row.notes ? <Icon fontSize="small" title={row.notes} sx={{ fontSize: 14, color: "#ed6c02" }}>sticky_note_2</Icon> : null}
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant="caption">{row.branch || "—"}</MDTypography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                          <Select
                            size="small"
                            displayEmpty
                            value={(row.internal_vendor || "").toLowerCase()}
                            sx={{ minWidth: 110, height: 32 }}
                            onChange={(ev) => saveVendor(row, ev.target.value)}
                          >
                            <MenuItem value="">— none —</MenuItem>
                            {vendors.map((v) => (
                              <MenuItem key={v._id} value={(v.key || "").toLowerCase()}>{v.name}</MenuItem>
                            ))}
                          </Select>
                          {vi?.has_api && <Chip label="API" size="small" color="success" sx={{ height: 18 }} />}
                        </MDBox>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <MDInput
                          value={awbEdits[row.parcel_id] ?? row.vendor_awb ?? ""}
                          placeholder="AWB"
                          onChange={(ev) => setAwbEdits((m) => ({ ...m, [row.parcel_id]: ev.target.value }))}
                          onBlur={(ev) => saveAwb(row, ev.target.value)}
                        />
                        {row.forwarding_no ? (
                          <MDTypography variant="caption" color="text" display="block">
                            fwd: {row.forwarding_no}
                          </MDTypography>
                        ) : null}
                        {!row.vendor_awb ? (
                          <MDTypography variant="caption" color="warning" display="block">
                            AWB missing
                          </MDTypography>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.weight || "—"}</TableCell>
                      <TableCell>{row.box_count || 0}</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <MDTypography variant="caption" display="block">
                          {row.last_message || row.last_status || "—"}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                          <MDTypography variant="caption" color={staleColor(row.days_since_update)}>
                            {row.days_since_update == null ? "no update yet" : `${row.days_since_update}d ago`}
                          </MDTypography>
                          {row.notify_status ? (
                            <Chip
                              size="small"
                              sx={{ height: 18 }}
                              title={`${row.notify_channel || ""} · ${row.notify_at ? new Date(row.notify_at).toLocaleString() : ""}`}
                              color={
                                ["read", "delivered"].includes(row.notify_status) ? "success"
                                  : row.notify_status === "failed" ? "error"
                                  : ["sent", "accepted"].includes(row.notify_status) ? "info"
                                  : "default"
                              }
                              label={`${row.notify_channel === "whatsapp" ? "WA" : row.notify_channel === "sms" ? "SMS" : ""} ${
                                { read: "read ✓✓", delivered: "delivered ✓✓", sent: "sent ✓", accepted: "queued", failed: "failed" }[row.notify_status] ||
                                row.notify_status
                              }`.trim()}
                            />
                          ) : null}
                        </MDBox>
                      </TableCell>
                    </TableRow>,
                    isOpen && (
                      <TableRow key={`${row.parcel_id}-d`}>
                        <TableCell
                          colSpan={14}
                          sx={{
                            p: 0,
                            backgroundColor: "#eaf3ff",
                            boxShadow: "inset 0 -2px 0 #1A73E8, inset 2px 0 0 #1A73E8, inset -2px 0 0 #1A73E8",
                          }}
                        >
                          <RowDetail
                            parcelId={row.parcel_id}
                            onPublished={load}
                            suggestion={row.suggestion}
                            busy={busy[row.suggestion ? row.suggestion._id : row.parcel_id]}
                            onApprove={(override) => approve(row, override)}
                            onReject={() => reject(row)}
                            onSuggest={() => suggestOne(row)}
                          />
                        </TableCell>
                      </TableRow>
                    ),
                  ];
                })}
                {visible.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={14}>
                    <MDTypography variant="button" color="text">No shipments match this filter.</MDTypography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </MDBox>
        </Card>
      </MDBox>
      {phrasesOpen && <PhrasesModal isopen={phrasesOpen} handleclose={() => setPhrasesOpen(false)} />}
      {vendorsOpen && <VendorsModal isopen={vendorsOpen} handleclose={() => { setVendorsOpen(false); getTrackingVendors().then((r) => setVendors(r.data || [])).catch(() => {}); }} />}
    </DashboardLayout>
  );
}
